<!--
	ButtonGroup: a segment bar, one bordered container with hairline dividers. Each direct child is a
	segment; give its trigger the `segment` (or `segmentCaret`) class. Direct `<dialog>` children are
	left out of the layout so a segment's modal can sit alongside it.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		/** The segments. Each top-level child becomes one equal-width segment. */
		children: Snippet;
		/** Accessible label for the group as a whole. */
		'aria-label'?: string;
	}

	const { children, 'aria-label': ariaLabel }: Props = $props();
</script>

<div class="buttonGroup" role="group" aria-label={ariaLabel}>
	{@render children()}
</div>

<style>
	/* A grid of equal 1fr tracks splits the bar evenly whatever the segment count; a long label only widens its own track if it can't fit. */
	.buttonGroup {
		display: grid;
		grid-auto-flow: row;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		background-color: var(--surface);
		overflow: hidden;

		:global(> *:not(dialog)) {
			display: flex;
			align-items: stretch;
			white-space: nowrap;
		}

		/* Dividers between adjacent segments only (so conditional segments leave no stray lines); a shadow, because segment triggers reset their borders. */
		> :global(*:not(dialog) + *:not(dialog)) {
			box-shadow: inset 0 1px 0 var(--border-strong);
		}

		:global(.segment),
		:global(.segmentCaret) {
			display: flex;
			justify-content: center;
			align-items: center;
			transition:
				background-color 0.12s ease-out,
				color 0.12s ease-out;
			cursor: pointer;
			border: 0;
			background: none;
			color: var(--text-color);
			user-select: none;

			&:hover {
				background-color: var(--hover-surface);
			}

			&:active {
				background-color: var(--active-surface);
			}

			&:focus-visible {
				outline-offset: -2px;
			}
		}

		:global(.segment) {
			flex: 1;
			gap: var(--space-2);
			padding: var(--space-2);
			min-height: var(--height-button);
			font-size: var(--font-size-base);
			line-height: 1.2;

			:global(svg) {
				flex-shrink: 0;
				width: var(--icon-sm);
				height: var(--icon-sm);
			}
		}

		/* Secondary trigger inside a segment, e.g. a split button's caret. */
		:global(.segmentCaret) {
			flex: 0 0 auto;
			padding: 0 var(--space-2);

			:global(svg) {
				width: var(--icon-xs);
				height: var(--icon-xs);
			}
		}

		/* Wide enough for the widest attributes panel, allowing for its padding and scrollbar. */
		@container attributes-actions (width > 500px) {
			/* Equal tracks, but never narrower than a segment's one-line label. */
			grid-auto-columns: minmax(max-content, 1fr);
			grid-auto-flow: column;

			> :global(*:not(dialog) + *:not(dialog)) {
				box-shadow: inset 1px 0 0 var(--border-strong);
			}
		}
	}
</style>
